import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { api, parseApiError, type ClanDetail, type ClanSeat } from '../lib/api';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

// ─────────────────────────────────────────────────────────────────────────────
// MatchRosterInitPanel — G3b-W0: the missing product path from "a clan has
// ghost seats" to "a match card is built from them". Shown on MatchCardLive
// while a match sits in DRAFT (no cardStatus yet, or cardStatus === 'DRAFT'):
// a leader/admin picks `teamSize` players PER CLAN straight off that clan's
// `GET /clan/:name` seats (ClanSeat[], each carrying its canonical
// resolveSeatPlayerId — see api.ts's ClanSeat.playerId doc comment) and posts
// them as clan1Roster/clan2Roster to the existing (previously never-called)
// POST /matchcard/:matchId/init (matchCardInit, handleInit in
// matchCardV2.ts). Ghost/pending seats are deliberately selectable — that's
// the whole point of this wave: a ghost's SLOT#/PLAYER_CARD rows end up keyed
// on the SAME id G3b-W1's claim-link (GHOST_LINK) stores, so a later claim or
// W2's alias read lines up with real match data instead of an ad-hoc
// demo-*/daily-* id.
// ─────────────────────────────────────────────────────────────────────────────

// ── Pure logic (unit-tested in MatchRosterInitPanel.test.ts) ────────────────

/**
 * Adds/removes `playerId` from a clan's roster-in-progress. A no-op once the
 * roster is already at `cap` and the id isn't already selected — the picker
 * enforces the max-squad ceiling by simply refusing further picks past the
 * cap, rather than bumping the oldest pick. Callers pass `teamSize` — the
 * MAXIMUM ALLOWED players on a team, set by comp rules.
 *
 * RULE REVERSED 2026-07-23 (Lisa): callers used to pass `teamSize + 2`
 * ("starters + up to 2 subs"). There is no starters/subs distinction — "they
 * are all team members and should be treated the same" — so the cap is simply
 * the comp's own maximum, and every picked player gets a slot whether or not
 * they end up playing.
 */
export function toggleSeatSelection(selected: readonly string[], playerId: string, cap: number): string[] {
  if (selected.includes(playerId)) {
    return selected.filter(id => id !== playerId);
  }
  if (selected.length >= cap) return [...selected];
  return [...selected, playerId];
}

/**
 * True once BOTH clans have at least one player picked, NEITHER exceeds
 * `teamSize`, and a schedule time is set.
 *
 * RULE REVERSED 2026-07-23 (Lisa). This used to require `teamSize` as a
 * MINIMUM and allow `teamSize + 2` as a maximum. `teamSize` is now the
 * MAXIMUM ALLOWED players on a team, set by comp rules — a ceiling, not a
 * quota. A side may legitimately field fewer ("it could be a different 4
 * playing each match if it's a team of 6"), and a side turning up short is a
 * competitive outcome, not a data defect. So the only floor is "somebody":
 * an empty squad has no slots and nothing to submit.
 */
export function isRosterInitReady(
  clan1Selected: readonly string[],
  clan2Selected: readonly string[],
  teamSize: number,
  scheduledAtStr: string,
): boolean {
  return clan1Selected.length >= 1 && clan1Selected.length <= teamSize
    && clan2Selected.length >= 1 && clan2Selected.length <= teamSize
    && scheduledAtStr.trim().length > 0;
}

/**
 * RULE REVERSED 2026-07-24 (T1b, Lisa): this used to be `deriveDefaultSelection`
 * — a Match-team-autofill (2026-07-13) that pre-ticked a team-entry side's
 * whole entered team, or a legacy side's whole seat pool when it fit inside
 * `teamSize`. Killed outright, everywhere a squad is picked (this panel AND
 * the agreement-time picker in CardAgreementPanel): "the captain must
 * explicitly pick who is fielding, and ONLY those ticked players get
 * match-card slots" (backend T1a doc comment, shared/matchCardAgreement.ts).
 * Registering 6 but fielding 4 must not leave 2 phantom players nagged and
 * flagged. Every seat now starts UNticked, full stop — no team-entry
 * shortcut, no small-pool shortcut.
 *
 * Clamps a competition's stored teamSize onto the three sizes the picker's
 * ceiling logic understands. Exported so CardAgreementPanel's own picker (the
 * squad tied to the AGREE action) uses the identical rule rather than a
 * second copy of the ternary.
 */
export function resolveTeamSizeCeiling(compTeamSize: number | null | undefined): 4 | 6 | 8 {
  return compTeamSize === 6 ? 6 : compTeamSize === 8 ? 8 : 4;
}

/**
 * The card's series format, derived from the COMPETITION's own seriesFormat
 * length. Pure so it carries a test.
 *
 * F4 (Lisa, 2026-07-23): "why is a format available? that is chosen as a comp
 * rule and should not be allowed to be changed match per match." This panel
 * previously held `useState<'BO3'|'BO5'|'BO7'>('BO3')` — a LITERAL that never
 * consulted the comp or the stage, both of which store a seriesFormat. A Bo5
 * competition silently built Bo3 cards unless an admin spotted the dropdown
 * and corrected it by hand.
 *
 * Falls back to BO3 for absent or unrepresentable lengths rather than
 * guessing — the card only knows how to plan 3, 5 or 7 games.
 */
export function formatFromSeriesLength(length: number | null | undefined): 'BO3' | 'BO5' | 'BO7' {
  if (length === 7) return 'BO7';
  if (length === 5) return 'BO5';
  return 'BO3';
}

/** yyyy-mm-ddThh:mm for a <input type="datetime-local"> from an epoch-ms timestamp, local tz. Mirrors CommandCenter.tsx's defaultDayTime. */
export function epochToLocalDateTimeInputValue(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function seatStatusLabel(status: ClanSeat['status']): string {
  if (status === 'CLAIMED') return 'Claimed';
  if (status === 'PENDING_CLAIM') return 'Pending';
  return 'Ghost';
}

// ── Presentation ─────────────────────────────────────────────────────────────

export function ClanSeatPicker({
  clanLabel, detail, isLoading, error, teamSize, maxSquad, selected, onToggle, restrictToTeamNo,
}: {
  clanLabel: string;
  detail?: ClanDetail;
  isLoading: boolean;
  error: unknown;
  teamSize: number;
  maxSquad: number;
  selected: readonly string[];
  onToggle: (playerId: string) => void;
  /**
   * T1b (2026-07-24): when set, only seats stamped with this teamNo are shown
   * — the agreement-time picker's own-team scope, so a captain can never even
   * SEE a stranger to tick (the backend would reject one as
   * FIELDED_SQUAD_STRANGER anyway; this just avoids the confusing round-trip).
   * Absent = the clan's whole seat pool, unfiltered — MatchRosterInitPanel's
   * manual/admin path deliberately keeps this, its escape hatch for picking
   * across teams when the automated path can't.
   */
  restrictToTeamNo?: number | null;
}) {
  const allSeats: ClanSeat[] = detail?.seats ?? [];
  const seats = restrictToTeamNo != null ? allSeats.filter(seat => seat.teamNo === restrictToTeamNo) : allSeats;
  // teamSize is a CEILING, not a target (2026-07-23) — any squad from 1 to
  // teamSize is valid, so this only dims the counter when nobody is picked.
  const hasPicks = selected.length >= 1;
  return (
    <div className="rounded-lg border border-iron-700 bg-iron-950 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="font-display text-xs font-bold uppercase tracking-wider text-iron-200">{clanLabel}</p>
        <span className={cn('font-mono text-[11px]', hasPicks ? 'text-iron-300' : 'text-iron-500')}>
          {selected.length}/{maxSquad} picked
          <span className="ml-1 text-iron-600">(max {teamSize} — set by the competition)</span>
        </span>
      </div>

      {isLoading && <p className="py-3 text-center"><Spinner className="mx-auto h-5 w-5" /></p>}
      {!!error && <p className="font-mono text-xs text-red-400">{parseApiError(error).message}</p>}

      {!isLoading && !error && seats.length === 0 && (
        <p className="font-mono text-xs text-iron-500">No seats yet for this clan — add seats from the Clans step first.</p>
      )}

      {!isLoading && seats.length > 0 && (
        <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
          {seats.map(seat => {
            const checked = selected.includes(seat.playerId);
            const atCap = !checked && selected.length >= maxSquad;
            return (
              <label
                key={seat.playerId}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 rounded-md bg-white/[0.03] px-2.5 py-1.5',
                  atCap && 'cursor-not-allowed opacity-50',
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={atCap}
                    onChange={() => onToggle(seat.playerId)}
                    className="h-3.5 w-3.5 shrink-0 accent-forge-400"
                  />
                  <span className="truncate font-mono text-xs text-ink-primary">{seat.gamertag}</span>
                  {seat.teamNo != null && (
                    <span className="shrink-0 font-mono text-[10px] text-iron-500">T{seat.teamNo}</span>
                  )}
                </span>
                <span className="shrink-0 font-display text-[9px] font-black uppercase tracking-[0.18em] text-iron-500">
                  {seatStatusLabel(seat.status)}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MatchRosterInitPanel({
  matchId, compId, clan1Name, clan2Name, clan1ClanId, clan2ClanId, clan1TeamNo, clan2TeamNo,
  defaultScheduledAt, compTeamSize, compSeriesLength, compName,
}: {
  matchId: string;
  compId: string;
  clan1Name: string;
  clan2Name: string;
  // Match-team-autofill (Lisa, 2026-07-13): the REAL clan pk backing each
  // side (match.clan1Id/clan2Id — see api.ts's MatchCardV2Response doc
  // comment) — clan1Name/clan2Name may be a qualified "Beta (Alpha)" display
  // string that GET /clan/:name can't resolve (clanSlug can't round-trip it
  // back to CLAN#ALPHA). Falls back to clan1Name/clan2Name when absent
  // (legacy side, where the bare name already IS the real clan identity).
  clan1ClanId?: string;
  clan2ClanId?: string;
  // Which of that clan's named teams is playing — shown as a "· Team N" label
  // next to that side's picker so a multi-team clan's leader knows which
  // squad they're on (T1b, 2026-07-24 — no longer drives a pre-fill; see
  // resolveTeamSizeCeiling's doc comment for why). Absent = legacy/whole-clan
  // side.
  clan1TeamNo?: number | null;
  clan2TeamNo?: number | null;
  defaultScheduledAt?: number;
  compTeamSize?: number;
  /**
   * The competition's own series length (`seriesFormat.length` — 3/5/7). The
   * card's format is DERIVED from this and is deliberately not editable per
   * match: the format is a competition rule (F4, Lisa 2026-07-23).
   */
  compSeriesLength?: number | null;
  compName?: string;
}) {
  const queryClient = useQueryClient();
  const teamSize = resolveTeamSizeCeiling(compTeamSize);
  // RULE REVERSED 2026-07-23 (Lisa): the cap was a fixed teamSize + 2
  // ("starters + up to 2 subs"). teamSize IS the maximum allowed players on a
  // team, set by comp rules — the org decides how many of those actually play.
  // handleInit (backend) enforces the same ceiling independently per clan.
  const maxSquad = teamSize;
  // F4 (2026-07-23): DERIVED from the competition, never chosen here. This was
  // a `useState('BO3')` literal — see formatFromSeriesLength's doc comment.
  const format = formatFromSeriesLength(compSeriesLength);
  const [scheduledAtStr, setScheduledAtStr] = useState(
    () => (defaultScheduledAt ? epochToLocalDateTimeInputValue(defaultScheduledAt) : ''),
  );
  // T1b (2026-07-24): starts empty, stays empty until the leader/admin taps a
  // checkbox — no one-shot pre-fill effect anymore (see resolveTeamSizeCeiling's
  // doc comment for why the old auto-fill was killed).
  const [clan1Selected, setClan1Selected] = useState<string[]>([]);
  const [clan2Selected, setClan2Selected] = useState<string[]>([]);

  const clan1Key = clan1ClanId ?? clan1Name;
  const clan2Key = clan2ClanId ?? clan2Name;
  const clan1Query = useQuery({ queryKey: ['clan', clan1Key], queryFn: () => api.clan(clan1Key) });
  const clan2Query = useQuery({ queryKey: ['clan', clan2Key], queryFn: () => api.clan(clan2Key) });

  const initMutation = useMutation({
    mutationFn: () => api.matchCardInit(matchId, {
      compId,
      format,
      scheduledAt: new Date(scheduledAtStr).getTime(),
      teamSize,
      clan1Roster: clan1Selected,
      clan2Roster: clan2Selected,
      ...(compName ? { compName } : {}),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['matchcard', matchId, compId] }),
  });

  const ready = isRosterInitReady(clan1Selected, clan2Selected, teamSize, scheduledAtStr);

  return (
    <section className="rounded-xl border border-iron-700 bg-iron-900 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-iron-200">
          <Users size={16} className="text-forge-300" /> Build The Match Card
        </h3>
        <span className="font-mono text-[11px] uppercase tracking-wider text-iron-500">{teamSize}v{teamSize}</span>
      </div>

      <p className="mb-3 font-mono text-xs text-iron-400">
        Pick the squad from each clan — up to {teamSize} players, the maximum this competition allows — to lock the
        roster and plan every game's submission slots for EVERY squad member. Nobody starts ticked; tick who's
        actually fielding.{' '}
        A sub who steps in later is already on the card and scores normally; anyone who doesn't play a given game just
        stays blank for it, no flag raised. Ghost and pending seats are selectable — that seat's canonical id carries
        through to its stats the moment a real player claims it.
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        <ClanSeatPicker
          clanLabel={clan1TeamNo != null ? `${clan1Name} · Team ${clan1TeamNo}` : clan1Name}
          detail={clan1Query.data}
          isLoading={clan1Query.isLoading}
          error={clan1Query.error}
          teamSize={teamSize}
          maxSquad={maxSquad}
          selected={clan1Selected}
          onToggle={id => setClan1Selected(prev => toggleSeatSelection(prev, id, maxSquad))}
        />
        <ClanSeatPicker
          clanLabel={clan2TeamNo != null ? `${clan2Name} · Team ${clan2TeamNo}` : clan2Name}
          detail={clan2Query.data}
          isLoading={clan2Query.isLoading}
          error={clan2Query.error}
          teamSize={teamSize}
          maxSquad={maxSquad}
          selected={clan2Selected}
          onToggle={id => setClan2Selected(prev => toggleSeatSelection(prev, id, maxSquad))}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-iron-500">Format</label>
          {/* Read-only by design (F4, Lisa 2026-07-23): the series format is a
              COMPETITION rule, not a per-match choice. Shown so an admin can
              see what the card will be built as, never edited here. */}
          <p className="font-display text-sm font-bold text-iron-100">
            {format === 'BO7' ? 'Best of 7' : format === 'BO5' ? 'Best of 5' : 'Best of 3'}
          </p>
          <p className="font-mono text-[10px] text-iron-500">set by the competition</p>
        </div>
        <div>
          <label className="mb-1 block font-mono text-[11px] uppercase tracking-wider text-iron-500">Scheduled at <span className="text-forge-400">*</span></label>
          <input
            type="datetime-local"
            value={scheduledAtStr}
            onChange={e => setScheduledAtStr(e.target.value)}
            className="input"
          />
        </div>
        <button
          onClick={() => initMutation.mutate()}
          disabled={!ready || initMutation.isPending}
          className="rounded-md bg-forge-600 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-forge-500 disabled:opacity-50"
        >
          {/* F1 (Lisa, 2026-07-23): "I don't see a place to upload" — this
              button IS the thing that opens uploads, and "Init match card"
              never said so. Name the outcome for the operator, not the
              internal route. */}
          {initMutation.isPending ? 'Locking…' : 'Lock squads & open uploads'}
        </button>
      </div>

      {!ready && (
        <p className="mt-2 font-mono text-[11px] text-iron-500">
          All fields are required — pick up to {teamSize} players per side and set a start
          time. The map veto stays optional after init.
        </p>
      )}

      {initMutation.isError && (
        <p className="mt-2 font-mono text-xs text-red-300">✗ {parseApiError(initMutation.error).message}</p>
      )}
    </section>
  );
}
